import React, { useState, useEffect, useCallback, useRef } from 'react';
import Header from './Header';
import CopyButton from './CopyButton';
import { api, TokenConvictionRankSlackAlert, TokenConvictionRankSlackAlertHistory, GetSlackAlertsQueryParams } from '../api';
import { formatNumberToLargestUnit, formatElapsedTime } from '../utils/formatters';
import { useDataStore } from '../stores/data/dataStore';
import { useUIStore } from '../stores/ui/uiStore';
import { useToasterStore } from '../stores/ui/toasterStore';
import { formatTimestampToLocaleString } from '../utils/time';

type SortField = 'latest_mc' | 'latest_cumulative_conviction_rank' | 'latest_peak_conviction_rank' | 'latest_time_to_reach_target_conviction_rank' | 'first_created_at' | 'last_updated_at' | 'total_alert_count';
type SortOrder = 'asc' | 'desc';

interface SearchParams {
  searchInput: string;
  isSearchMode: boolean;
  searchTab: 'address' | 'date';
  dateFrom: string;
  dateTo: string;
  page: number;
  sortBy: SortField;
  sortOrder: SortOrder;
}

const INITIAL_SEARCH_PARAMS: SearchParams = {
  searchInput: '',
  isSearchMode: false,
  searchTab: 'address',
  dateFrom: '',
  dateTo: '',
  page: 1,
  sortBy: 'last_updated_at',
  sortOrder: 'desc',
};

const AlertsPage: React.FC = () => {
  const getTokenData = useDataStore((state) => state.getTokenData);
  const { showToaster } = useToasterStore();
  const { setSelectedToken, openTokenDetailModal } = useUIStore();
  const [alerts, setAlerts] = useState<TokenConvictionRankSlackAlert[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  
  // 검색 관련 파라미터를 하나의 객체로 통합
  const [searchParams, setSearchParams] = useState<SearchParams>(INITIAL_SEARCH_PARAMS);
  
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  // AbortController를 위한 ref
  const abortControllerRef = useRef<AbortController | null>(null);

  // 리스트 조회
  const fetchAlerts = useCallback(async (params: SearchParams) => {
    // 이전 요청이 있으면 취소
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // 새로운 AbortController 생성
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsLoading(true);
    setError(null);
    try {
      const queryParams: GetSlackAlertsQueryParams = {
        page: params.page,
        limit,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
      };

      // 날짜 필터 파라미터 추가
      if (params.dateFrom) {
        queryParams.from = new Date(params.dateFrom).toISOString();
      }
      if (params.dateTo) {
        const toDate = new Date(params.dateTo);
        toDate.setHours(23, 59, 59, 999);
        queryParams.to = toDate.toISOString();
      }

      const response = await api.getSlackAlerts(queryParams, abortController.signal);
      
      // 요청이 취소되었는지 확인
      if (abortController.signal.aborted) {
        return;
      }

      setAlerts(response.data);
      setTotalPages(response.totalPages);
      setTotal(response.total);
      
      // 검색 모드가 아닌 경우에만 isSearchMode를 false로 설정
      if (!params.isSearchMode) {
        setSearchParams(prev => ({ ...prev, isSearchMode: false }));
      }
    } catch (err: any) {
      // AbortError는 무시 (의도적인 취소)
      // axios에서 취소된 요청의 경우 여러 방식으로 확인
      const isCanceled =
        err.name === 'AbortError' ||
        err.name === 'CanceledError' ||
        err.code === 'ERR_CANCELED' ||
        err.message === 'canceled' ||
        (err.response === undefined && err.request && abortController.signal.aborted);
      
      if (isCanceled) {
        return;
      }
      setError(err.message || '알림 목록을 불러오는데 실패했습니다.');
      console.error('Failed to fetch alerts:', err);
    } finally {
      // 현재 요청이 취소되지 않았을 때만 로딩 상태 해제
      if (!abortController.signal.aborted) {
        setIsLoading(false);
        abortControllerRef.current = null;
      }
    }
  }, [limit]);

  // 개별 조회
  const handleSearch = useCallback(async () => {
    const trimmedQuery = searchParams.searchInput.trim();
    if (!trimmedQuery) {
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const alert = await api.getSlackAlertByTokenAddress(trimmedQuery);
      if (alert && alert.token_address) {
        setAlerts([alert]);
        setTotalPages(1);
        setTotal(1);
        setSearchParams(prev => ({ ...prev, isSearchMode: true }));
        setError(null);
      } else {
        setAlerts([]);
        setTotalPages(0);
        setTotal(0);
        setSearchParams(prev => ({ ...prev, isSearchMode: true }));
        setError('해당 토큰 주소의 알림을 찾을 수 없습니다.');
      }
    } catch (err: any) {
      setError(err.message || '알림을 조회하는데 실패했습니다. 올바른 토큰 주소를 입력해주세요.');
      setAlerts([]);
      setTotalPages(0);
      setTotal(0);
      setSearchParams(prev => ({ ...prev, isSearchMode: true }));
      console.error('Failed to search alert:', err);
    } finally {
      setIsLoading(false);
    }
  }, [searchParams.searchInput]);

  // 검색 초기화
  const handleClearSearch = useCallback(() => {
    const newParams = { ...INITIAL_SEARCH_PARAMS, searchTab: searchParams.searchTab };
    setSearchParams(newParams);
    fetchAlerts(newParams);
  }, [fetchAlerts, searchParams.searchTab]);


  // 탭 변경 핸들러
  const handleTabChange = useCallback((tab: 'address' | 'date') => {
    setSearchParams(prev => {
      const newParams = {
        ...INITIAL_SEARCH_PARAMS,
        searchTab: tab,
        page: 1,
        sortBy: prev.sortBy,
        sortOrder: prev.sortOrder,
      };
      fetchAlerts(newParams);
      return newParams;
    });
  }, [fetchAlerts]);

  // Enter 키로 검색
  const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }, [handleSearch]);

  // 정렬 핸들러
  const handleSort = useCallback((field: SortField) => {
    setSearchParams(prev => {
      const newSortOrder = prev.sortBy === field 
        ? (prev.sortOrder === 'asc' ? 'desc' : 'asc')
        : 'desc';
      
      return {
        ...prev,
        sortBy: field,
        sortOrder: newSortOrder,
      };
    });
  }, []);

  // 페이지 변경 핸들러
  const handlePageChange = useCallback((newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setSearchParams(prev => ({ ...prev, page: newPage }));
  }, [totalPages]);

  // 행 클릭 핸들러
  const handleRowClick = useCallback((alertId: string) => {
    setExpandedRowId(prev => prev === alertId ? null : alertId);
  }, []);

  // 토큰 디테일 모달 열기 핸들러
  const handleOpenTokenDetailModal = useCallback(async (e: React.MouseEvent, alert: TokenConvictionRankSlackAlert) => {
    e.stopPropagation(); // 행 클릭 이벤트 전파 방지
    if (alert.token_address) {
      // 토큰 주소를 클립보드에 복사
      try {
        await navigator.clipboard.writeText(alert.token_address);
        showToaster('Token address copied to clipboard!', 'success');
      } catch (error) {
        console.error('클립보드 복사 실패:', error);
        showToaster('Failed to copy to clipboard.', 'error');
      }
      setSelectedToken({
        tokenAddress: alert.token_address,
        symbol: alert.token_symbol || 'Unknown',
      });
      openTokenDetailModal();
    }
  }, [showToaster, setSelectedToken, openTokenDetailModal]);

  // 검색 파라미터 변경 시 API 재조회 (검색 모드가 아닐 때만)
  useEffect(() => {
    if (!searchParams.isSearchMode) {
      fetchAlerts(searchParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.page, searchParams.sortBy, searchParams.sortOrder, searchParams.dateFrom, searchParams.dateTo, searchParams.isSearchMode]);

  // 초기 로드
  useEffect(() => {
    fetchAlerts(INITIAL_SEARCH_PARAMS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 30초마다 폴링 (검색 모드가 아닐 때만)
  useEffect(() => {
    if (searchParams.isSearchMode) {
      return; // 검색 모드일 때는 폴링하지 않음
    }

    const intervalId = setInterval(() => {
      fetchAlerts(searchParams);
    }, 30000); // 30초마다 폴링

    // 컴포넌트 언마운트 시 또는 검색 모드로 변경 시 interval 정리
    return () => {
      clearInterval(intervalId);
    };
  }, [fetchAlerts, searchParams, searchParams.isSearchMode]);

  // 정렬 아이콘
  const getSortIcon = useCallback((field: SortField) => {
    if (searchParams.sortBy !== field) {
      return (
        <span className="text-gray-500 text-xs">↕</span>
      );
    }
    return searchParams.sortOrder === 'asc' ? (
      <span className="text-amber-300 text-xs">↑</span>
    ) : (
      <span className="text-amber-300 text-xs">↓</span>
    );
  }, [searchParams.sortBy, searchParams.sortOrder]);

  // histories 정렬 (created_at 최신순)
  const getSortedHistories = useCallback((histories: TokenConvictionRankSlackAlertHistory[]) => {
    if (!histories || !Array.isArray(histories)) {
      return [];
    }
    return [...histories]
      .filter((h) => h && h.created_at)
      .sort((a, b) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateB - dateA; // 최신순
      });
  }, []);

  // MC 변화 추적: 최신 값과 두 번째 최신 값 비교
  const getMcChangeColor = useCallback((alert: TokenConvictionRankSlackAlert): string | null => {
    if (!alert.histories || !Array.isArray(alert.histories) || alert.histories.length < 2) {
      return null; // 비교할 데이터가 없으면 null 반환
    }

    const sortedHistories = getSortedHistories(alert.histories);
    const latestMc = sortedHistories[0]?.mc;
    const previousMc = sortedHistories[1]?.mc;

    if (latestMc === undefined || previousMc === undefined) {
      return null;
    }

    if (latestMc > previousMc) {
      return 'text-green-400'; // 상승: 초록색
    } else if (latestMc < previousMc) {
      return 'text-red-400'; // 하락: 빨간색
    }

    return null; // 변화 없음
  }, [getSortedHistories]);

  // 날짜 포맷팅
  const formatDate = (dateString: string) =>
    formatTimestampToLocaleString(dateString, 'en-US', {
      timeZone: 'Asia/Seoul',
    });

  // 토큰 age 계산 (pool_created_at부터 현재까지)
  const getTokenAge = (poolCreatedAt: string | Date): string => {
    if (!poolCreatedAt) return 'N/A';
    return formatElapsedTime(poolCreatedAt, new Date());
  };

  // 강제 새로고침 핸들러
  const handleRefresh = useCallback(() => {
    if (searchParams.isSearchMode) {
      // 검색 모드일 때는 검색 재실행
      handleSearch();
    } else {
      // 일반 모드일 때는 현재 파라미터로 fetchAlerts 호출
      fetchAlerts(searchParams);
    }
  }, [searchParams, fetchAlerts, handleSearch]);

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header title="Alerts" showPageControls={false} />
      <div className="container mx-auto py-8 px-4">
        <div className="mx-auto">
          <div className="mb-6">
              <div className="mb-4 flex items-center justify-between">
                <h1 className="text-2xl font-bold text-white mb-2">Slack Alerts</h1>
                <button
                  onClick={handleRefresh}
                  disabled={isLoading}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="새로고침"
                >
                  <svg
                    className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>
              </div>

            {/* 검색 탭 영역 */}
            <div className="mb-4">
              {/* 탭 버튼 */}
              <div className="flex gap-2 mb-4 border-b border-gray-700">
                <button
                  onClick={() => handleTabChange('address')}
                  className={`px-4 py-2 font-medium transition-colors ${
                    searchParams.searchTab === 'address'
                      ? 'text-amber-300 border-b-2 border-amber-300'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Token Address Search
                </button>
                <button
                  onClick={() => handleTabChange('date')}
                  className={`px-4 py-2 font-medium transition-colors ${
                    searchParams.searchTab === 'date'
                      ? 'text-amber-300 border-b-2 border-amber-300'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Date Range Search
                </button>
              </div>

              {/* 토큰 주소 검색 탭 */}
              {searchParams.searchTab === 'address' && (
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={searchParams.searchInput}
                      onChange={(e) => setSearchParams(prev => ({ ...prev, searchInput: e.target.value }))}
                      onKeyPress={handleKeyPress}
                      placeholder="Search by Token Address..."
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {searchParams.searchInput && (
                      <button
                        onClick={handleClearSearch}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                        aria-label="검색 초기화"
                      >
                        <svg 
                          className="w-5 h-5" 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M6 18L18 6M6 6l12 12" 
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                  <button
                    onClick={handleSearch}
                    disabled={isLoading}
                    className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? '검색 중...' : '검색'}
                  </button>
                </div>
              )}

              {/* 날짜 범위 검색 탭 */}
              {searchParams.searchTab === 'date' && (
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <label className="block text-sm text-gray-400 mb-1">From (Start Date)</label>
                    <input
                      type="date"
                      value={searchParams.dateFrom}
                      onChange={(e) => setSearchParams(prev => ({ ...prev, dateFrom: e.target.value }))}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm text-gray-400 mb-1">To (End Date)</label>
                    <input
                      type="date"
                      value={searchParams.dateTo}
                      onChange={(e) => setSearchParams(prev => ({ ...prev, dateTo: e.target.value }))}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex gap-2">
                    {/* <button
                      onClick={handleApplyDateFilter}
                      disabled={isLoading}
                      className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? '검색 중...' : '검색'}
                    </button> */}
                    {(searchParams.dateFrom || searchParams.dateTo) && (
                      <button
                        onClick={handleClearSearch}
                        className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-900"
                      >
                        초기화
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* 검색 모드 표시 */}
            {searchParams.isSearchMode && (
              <div className="mb-4 flex items-center justify-between bg-gray-800 px-4 py-2 rounded-md">
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 text-sm">검색 모드:</span>
                  <span className="text-white font-medium">
                    {searchParams.searchTab === 'address' 
                      ? searchParams.searchInput 
                      : `${searchParams.dateFrom || '전체'} ~ ${searchParams.dateTo || '전체'}`}
                  </span>
                </div>
                <button
                  onClick={handleClearSearch}
                  className="text-gray-400 hover:text-white text-sm transition-colors"
                >
                  검색 초기화
                </button>
              </div>
            )}

            {/* 에러 메시지 */}
            {error && (
              <div className="mb-4 p-4 bg-red-900/30 border border-red-700 rounded-md text-red-200">
                {error}
              </div>
            )}
          </div>

          {/* 테이블 */}
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            {isLoading && alerts.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                Loading...
              </div>
            ) : alerts.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                  No alert data found.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <div className="overflow-y-auto max-h-[calc(100vh-400px)]">
                    <table className="w-full border-collapse">
                      <thead className="sticky top-0 z-10 bg-gray-800">
                        <tr className="border-b border-gray-700">
                        <th className="text-center py-3 px-4 text-gray-300 font-semibold">
                          <span></span>
                        </th>
                        <th className="text-left py-3 px-4 text-gray-300 font-semibold">
                          <span>Token Address</span>
                        </th>
                        <th className="text-left py-3 px-4 text-gray-300 font-semibold">
                          <span>Symbol</span>
                        </th>
                        <th className="text-right py-3 px-4 text-gray-300 font-semibold">
                          <button
                            onClick={() => handleSort('latest_mc')}
                            className="flex items-center space-x-1 hover:text-white transition-colors ml-auto"
                          >
                            <span>MC</span>
                            {getSortIcon('latest_mc')}
                          </button>
                        </th>
                        <th className="text-right py-3 px-4 text-gray-300 font-semibold">
                          <button
                            onClick={() => handleSort('latest_cumulative_conviction_rank')}
                            className="flex items-center space-x-1 hover:text-white transition-colors ml-auto"
                          >
                            <span>Cumulative (%)</span>
                            {getSortIcon('latest_cumulative_conviction_rank')}
                          </button>
                        </th>
                        <th className="text-right py-3 px-4 text-gray-300 font-semibold">
                          <button
                            onClick={() => handleSort('latest_peak_conviction_rank')}
                            className="flex items-center space-x-1 hover:text-white transition-colors ml-auto"
                          >
                            <span>Peak (%)</span>
                            {getSortIcon('latest_peak_conviction_rank')}
                          </button>
                        </th>
                        <th className="text-right py-3 px-4 text-gray-300 font-semibold">
                          <button
                            onClick={() => handleSort('latest_time_to_reach_target_conviction_rank')}
                            className="flex items-center space-x-1 hover:text-white transition-colors ml-auto"
                          >
                            <span>Target (%)</span>
                            {getSortIcon('latest_time_to_reach_target_conviction_rank')}
                          </button>
                        </th>
                        <th className="text-right py-3 px-4 text-gray-300 font-semibold">
                          <button
                            onClick={() => handleSort('total_alert_count')}
                            className="flex items-center space-x-1 hover:text-white transition-colors ml-auto"
                          >
                            <span>Alert Count</span>
                            {getSortIcon('total_alert_count')}
                          </button>
                        </th>
                        <th className="text-left py-3 px-4 text-gray-300 font-semibold">
                          <button
                            onClick={() => handleSort('first_created_at')}
                            className="flex items-center space-x-1 hover:text-white transition-colors"
                          >
                            <span>First Created</span>
                            {getSortIcon('first_created_at')}
                          </button>
                        </th>
                        <th className="text-left py-3 px-4 text-gray-300 font-semibold">
                          <button
                            onClick={() => handleSort('last_updated_at')}
                            className="flex items-center space-x-1 hover:text-white transition-colors"
                          >
                            <span>Last Tx Time</span>
                            {getSortIcon('last_updated_at')}
                          </button>
                        </th>
                        <th className="text-center py-3 px-4 text-gray-300 font-semibold">
                          <span>Age</span>
                        </th>
                        <th className="text-center py-3 px-4 text-gray-300 font-semibold">
                          <span></span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {alerts
                        .filter((alert) => alert && alert._id && alert.token_address)
                        .map((alert) => (
                        <React.Fragment key={alert._id}>
                          <tr
                            onClick={() => handleRowClick(alert._id)}
                            className="border-b border-gray-700 hover:bg-gray-750 cursor-pointer transition-colors"
                          >
                            <td className="py-3 px-4 text-center">
                              {(() => {
                                const tokenData = alert.token_address ? getTokenData(alert.token_address) : null;
                                const imageUrl = tokenData?.tokenInfo?.imageUrl;
                                return imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt={alert.token_symbol || 'Token'}
                                    className="w-8 h-8 rounded-full object-cover"
                                    onError={(e) => {
                                      // 이미지 로드 실패 시 기본 이미지로 대체
                                      (e.target as HTMLImageElement).src = 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQYoWXFc1ZD2ULUuOL8Xtb8V8rizl57AIB3rw&s';
                                    }}
                                  />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
                                    <span className="text-xs text-gray-400">?</span>
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center space-x-2">
                                <span className="font-mono text-sm">
                                  {alert.token_address ? `${alert.token_address.slice(0, 8)}...` : 'N/A'}
                                </span>
                                {alert.token_address && <CopyButton text={alert.token_address} />}
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <span className="font-semibold">{alert.token_symbol || 'N/A'}</span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className={`font-mono ${getMcChangeColor(alert) || 'text-gray-300'}`}>
                                ${formatNumberToLargestUnit(alert.latest_mc ?? 0)}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className="font-mono">
                                {((alert.latest_cumulative_conviction_rank ?? 0) * 100).toFixed(2)}%
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className="font-mono">
                                {((alert.latest_peak_conviction_rank ?? 0) * 100).toFixed(2)}%
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className="font-mono">
                                {((alert.latest_time_to_reach_target_conviction_rank ?? 0) * 100).toFixed(2)}%
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className="font-mono">{alert.total_alert_count ?? 0}</span>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-400">
                              {alert.first_created_at ? formatDate(alert.first_created_at) : 'N/A'}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-400">
                              {alert.last_updated_at ? formatDate(alert.last_updated_at) : 'N/A'}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="font-mono text-sm text-gray-300">
                                {getTokenAge(alert.pool_created_at)}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <button
                                onClick={(e) => handleOpenTokenDetailModal(e, alert)}
                                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800"
                                title="토큰 상세 정보 보기"
                              >
                                Details
                              </button>
                            </td>
                          </tr>
                          {expandedRowId === alert._id && alert.histories && Array.isArray(alert.histories) && alert.histories.length > 0 && (
                            <tr>
                              <td colSpan={12} className="py-4 px-4 bg-gray-900">
                                <div className="space-y-2">
                                  <h4 className="text-sm font-semibold text-gray-300 mb-3">
                                    Histories ({alert.histories.length}개)
                                  </h4>
                                  <div className="overflow-x-auto">
                                    <table className="w-full border-collapse">
                                      <thead>
                                        <tr className="border-b border-gray-600">
                                        <th className="text-left py-2 px-3 text-xs text-gray-400">AF Address</th>
                                          <th className="text-left py-2 px-3 text-xs text-gray-400">MC</th>
                                          <th className="text-left py-2 px-3 text-xs text-gray-400">Cumulative (%)</th>
                                          <th className="text-left py-2 px-3 text-xs text-gray-400">Peak (%)</th>
                                          <th className="text-left py-2 px-3 text-xs text-gray-400">Target (%)</th>
                                          <th className="text-left py-2 px-3 text-xs text-gray-400">Created At</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {getSortedHistories(alert.histories)
                                          .filter((history) => history && history.af_address)
                                          .map((history, idx) => (
                                          <tr key={idx} className="border-b border-gray-700">
                                             <td className="py-2 px-3 text-sm">
                                              <div className="flex items-center space-x-2">
                                                <span className="font-mono text-xs">
                                                  {history.af_address ? `${history.af_address.slice(0, 8)}...` : 'N/A'}
                                                </span>
                                                {history.af_address && <CopyButton text={history.af_address} />}
                                              </div>
                                            </td>
                                            <td className="py-2 px-3 text-sm">
                                              <span className="font-mono">
                                                ${formatNumberToLargestUnit(history.mc ?? 0)}
                                              </span>
                                            </td>
                                            <td className="py-2 px-3 text-sm">
                                              <span className="font-mono">
                                                {((history.cumulative_conviction_rank ?? 0) * 100).toFixed(2)}%
                                              </span>
                                            </td>
                                            <td className="py-2 px-3 text-sm">
                                              <span className="font-mono">
                                                {((history.peak_conviction_rank ?? 0) * 100).toFixed(2)}%
                                              </span>
                                            </td>
                                            <td className="py-2 px-3 text-sm">
                                              <span className="font-mono">
                                                {((history.time_to_reach_target_conviction_rank ?? 0) * 100).toFixed(2)}%
                                              </span>
                                            </td>
                                            <td className="py-2 px-3 text-sm text-gray-400">
                                              {history.created_at ? formatDate(history.created_at) : 'N/A'}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* 페이지네이션 */}
                {!searchParams.isSearchMode && totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700 bg-gray-800">
                    <div className="text-sm text-gray-400">
                      Total {total} items, showing {((searchParams.page - 1) * limit) + 1}-{Math.min(searchParams.page * limit, total)}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(searchParams.page - 1)}
                        disabled={searchParams.page === 1}
                        className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-1"
                        title="이전 페이지"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        <span>Previous</span>
                      </button>
                      <span className="text-sm text-gray-300 px-3">
                        {searchParams.page} / {totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(searchParams.page + 1)}
                        disabled={searchParams.page === totalPages}
                        className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-1"
                        title="다음 페이지"
                      >
                        <span>Next</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertsPage;

